import React, { useState, useEffect } from 'react';
import { Question, QuestionProps } from '../types/assessment';

export const QuestionComponent: React.FC<QuestionProps> = ({
  question,
  value,
  onChange,
  onNext,
  onPrevious,
  canGoBack,
  isLast,
  validation
}) => {
  const [localValue, setLocalValue] = useState<number | string | string[]>(value || '');
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    setLocalValue(value || '');
    setHasInteracted(false);
  }, [question.id, value]);

  const handleChange = (newValue: number | string | string[]) => {
    setLocalValue(newValue);
    setHasInteracted(true);
    onChange(newValue);
    
    // For auto-advance questions, trigger next automatically after a short delay
    // This is handled in the parent AssessmentFlow component
  };

  const handleNext = () => {
    if (isValid()) {
      onNext();
    }
  };

  const isValid = (): boolean => {
    if (!question.validation.required) return true;
    
    if (question.type === 'scale') {
      return typeof localValue === 'number' && localValue >= question.scale!.min && localValue <= question.scale!.max;
    }
    
    if (question.type === 'multiple_choice') {
      const selections = Array.isArray(localValue) ? localValue : [];
      return selections.length > 0 && (!question.validation.max_selections || selections.length <= question.validation.max_selections);
    }
    
    if (question.type === 'single_choice') {
      return localValue !== '' && localValue !== undefined;
    }
    
    if (question.type === 'text') {
      const text = String(localValue);
      return text.length >= (question.validation.min_length || 1);
    }
    
    return false;
  };

  const renderQuestionInput = () => {
    switch (question.type) {
      case 'scale':
        return (
          <ScaleInput
            question={question}
            value={typeof localValue === 'number' ? localValue : question.scale!.min}
            onChange={handleChange}
          />
        );
      
      case 'single_choice':
        return (
          <SingleChoiceInput
            question={question}
            value={String(localValue)}
            onChange={handleChange}
          />
        );
      
      case 'multiple_choice':
        return (
          <MultipleChoiceInput
            question={question}
            value={Array.isArray(localValue) ? localValue : []}
            onChange={handleChange}
          />
        );
      
      case 'text':
        return (
          <TextInput
            question={question}
            value={String(localValue)}
            onChange={handleChange}
          />
        );
      
      default:
        return <div>Nepodporovaný typ otázky</div>;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mx-auto max-w-2xl question-shell">
      {/* Question Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">
          {question.title}
        </h2>
        {question.description && (
          <p className="text-gray-600 text-lg leading-relaxed">
            {question.description}
          </p>
        )}
      </div>

      {/* Question Input */}
      <div className="mb-8">
        {renderQuestionInput()}
      </div>

      {/* Validation Errors */}
      {hasInteracted && validation && !validation.isValid && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          {validation.errors.map((error, index) => (
            <p key={index} className="text-red-600 text-sm">
              {error.message}
            </p>
          ))}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="question-nav">
        <button
          onClick={onPrevious}
          disabled={!canGoBack}
          className={`question-btn question-btn-secondary ${canGoBack ? '' : 'is-disabled'}`}
        >
          ← Zpět
        </button>

        <button
          onClick={handleNext}
          disabled={!isValid()}
          className={`question-btn question-btn-primary ${isValid() ? '' : 'is-disabled'}`}
        >
          {isLast ? 'Dokončit' : 'Pokračovat'} →
        </button>
      </div>
    </div>
  );
};

// Scale Input Component
const ScaleInput: React.FC<{
  question: Question;
  value: number;
  onChange: (value: number) => void;
}> = ({ question, value, onChange }) => {
  const scale = question.scale!;
  const range = scale.max - scale.min;
  const steps = Array.from({ length: range + 1 }, (_, i) => scale.min + i);

  return (
    <div className="scale-wrapper">
      {/* Slider */}
      <div className="scale-slider">
        <input
          type="range"
          min={scale.min}
          max={scale.max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="scale-range"
        />
        
        {/* Scale Labels */}
        <div className="scale-labels">
          <span>{scale.min_label}</span>
          <span>{scale.max_label}</span>
        </div>
      </div>

      {/* Number Buttons */}
      <div className="scale-steps">
        {steps.map((step) => (
          <button
            key={step}
            onClick={() => onChange(step)}
            className={`scale-step ${value === step ? 'is-selected' : ''}`}
          >
            {step}
          </button>
        ))}
      </div>
    </div>
  );
};

// Single Choice Input Component
const SingleChoiceInput: React.FC<{
  question: Question;
  value: string;
  onChange: (value: string) => void;
}> = ({ question, value, onChange }) => {
  return (
    <div className="space-y-3">
      {question.options?.map((option) => (
        <label
          key={option.value}
          className={`option-row ${value === option.value ? 'is-selected' : ''}`}
        >
          <div className="option-row-inner">
            <input
              type="radio"
              name={question.id}
              value={String(option.value)}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
              className="option-radio-input"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">{option.label}</div>
              {option.description && (
                <div className="text-sm text-gray-600 mt-1">{option.description}</div>
              )}
            </div>
          </div>
        </label>
      ))}
    </div>
  );
};

// Multiple Choice Input Component
const MultipleChoiceInput: React.FC<{
  question: Question;
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ question, value, onChange }) => {
  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    
    // Check max selections
    if (question.validation.max_selections && newValue.length > question.validation.max_selections) {
      return;
    }
    
    onChange(newValue);
  };

  return (
    <div className="space-y-3">
      {question.validation.max_selections && (
        <p className="text-sm text-gray-600 mb-4">
          Vyberte maximálně {question.validation.max_selections} možností
        </p>
      )}
      
      {question.options?.map((option) => {
        const isSelected = value.includes(String(option.value));
        const isDisabled = !isSelected && question.validation.max_selections && value.length >= question.validation.max_selections ? true : false;
        
        return (
          <label
            key={option.value}
            className={`option-row ${isSelected ? 'is-selected' : ''} ${isDisabled ? 'is-disabled' : ''}`}
          >
            <div className="option-row-inner">
              <input
                type="checkbox"
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => handleToggle(String(option.value))}
                className="option-radio-input"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{option.label}</div>
                {option.description && (
                  <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                )}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};

// Text Input Component
const TextInput: React.FC<{
  question: Question;
  value: string;
  onChange: (value: string) => void;
}> = ({ question, value, onChange }) => {
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Napište svou odpověď..."
        rows={4}
        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none"
      />
      {question.validation.min_length && (
        <p className="text-sm text-gray-500 mt-2">
          Minimálně {question.validation.min_length} znaků
          {value.length > 0 && ` (${value.length})`}
        </p>
      )}
    </div>
  );
};

export default QuestionComponent;
